import { UIElement } from '../ui-element';
import { element, html } from '../../global/template-literals';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import styles from './ui-header-content.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIHeaderContent
 * @element ui-header-content
 * @classdesc Represents a class for <code>ui-header-content</code> element.
 * @property {string} [logoSrc] {@attr logo-src} src of image that will be attached to the link with logo.
 * @property {string} [logoHref] {@attr logo-href} href that will be attached to the link with logo image.
 * @property {string} [navFor] {@attr nav-for} proxy to ui-navtoggle 'for'-attribute:
 * if presented - ui-navtoggle on mobile with burger image will be created.
 * @property {string} [navGroup] {@attr nav-group} proxy to ui-navtoggle 'group'-attribute.
 * @slot {@name environment} Environment selector slot.
 * @example
 * <ui-header-content logo-src="webcomponents/%WEB_COMPONENTS_VERSION%/assets/images/swedbank-logo.svg"
 *      logo-href="index.html" nav-for="nav-main" nav-group="navigation">
 * </ui-header-content>
 */
@Component({ tag: 'ui-header-content', styles })
export class UIHeaderContent extends UIElement {
    private resizeObserver?: ResizeObserver;
    private readonly resizeHandler = this.setNavTop.bind(this);

    @Attribute(String) logoSrc?: string;
    @Attribute(String) logoHref?: string;
    @Attribute(String) navFor?: string;
    @Attribute(String) navGroup?: string;

    /**
     * Set CSS property --ui-nav-top, needed for ui-nav
     * and ui-navtoggle on mobile
     */
    setNavTop(): void {
        const header = this.getRenderedHeader();
        if (header.getClientRects().length === 0) {
            return;
        }
        document.documentElement.style.setProperty(
            '--ui-nav-top',
            header.getBoundingClientRect().bottom + 'px'
        );
    }

    private getRenderedHeader(): Element {
        return (
            this.closest('ui-header')?.querySelector(':scope > header') ?? this
        );
    }

    private observeHeaderResize(): void {
        if (typeof ResizeObserver === 'undefined') {
            return;
        }
        this.resizeObserver ??= new ResizeObserver(this.resizeHandler);
        this.resizeObserver.observe(this.getRenderedHeader());
    }

    /**
     * Builds link element with logo.
     * @returns {HTMLElement}
     */
    buildLogo(): HTMLElement {
        return this.createElement({
            tagName: this.logoHref ? 'a' : 'div',
            attributes: {
                class: 'ui-header-content__logo',
                href: this.logoHref ? this.logoHref : null,
            },
            children: [
                {
                    tagName: 'img',
                    attributes: {
                        src: this.logoSrc,
                        alt: 'Swedbank logo',
                    },
                },
            ],
        });
    }

    /**
     * Builds menu navtoggle on mobile.
     * @returns {HTMLElement}
     */
    buildMenuNavtoggle(): HTMLElement {
        return element`
          <ui-navtoggle class="ui-header-content__menu" for="${this.navFor}" group="${this.navGroup}"></ui-navtoggle>
        `;
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        const environmentToggle = this.getChildrenForSlot('environment')[0];
        const contentNodes = this.detachChildNodes();

        environmentToggle?.classList.add('ui-header-content__environment');

        this.append(
            html` ${this.logoSrc && this.buildLogo()} ${environmentToggle}
            ${contentNodes} ${this.navFor && this.buildMenuNavtoggle()}`
        );
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        this.setNavTop();
        this.observeHeaderResize();
        window.addEventListener('resize', this.resizeHandler);
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        this.resizeObserver?.disconnect();
        window.removeEventListener('resize', this.resizeHandler);
    }

    /**
     * @inheritDoc
     */
    override reconnect(): void {
        this.observeHeaderResize();
        window.addEventListener('resize', this.resizeHandler);
        this.setNavTop();
    }
}
